import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquarePlus, X, Bug, Lightbulb, Sparkles, Camera, Loader2, CheckCircle, AlertCircle, History, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { betaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import html2canvas from 'html2canvas';

const BetaFeedbackButton = () => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [betaEnabled, setBetaEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('feedback');
  const [myFeedback, setMyFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    category: 'bug',
    title: '',
    description: '',
  });

  // Check beta settings on mount
  useEffect(() => {
    const checkBetaSettings = async () => {
      try {
        const settings = await betaAPI.getSettings();
        setBetaEnabled(settings.is_active);
      } catch (error) {
        console.error('Failed to check beta settings:', error);
        setBetaEnabled(false);
      }
    };
    checkBetaSettings();
  }, []);

  // Fetch user's feedback history
  const fetchMyFeedback = useCallback(async () => {
    if (!isAuthenticated || !user?.is_beta_tester) return;
    
    setLoadingFeedback(true);
    try {
      // Use user email as identifier
      const feedback = await betaAPI.getMyFeedbackByEmail(user.email);
      setMyFeedback(feedback || []);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoadingFeedback(false);
    }
  }, [isAuthenticated, user]);

  // Capture screenshot when opening feedback form
  const captureScreenshot = useCallback(async () => {
    setCapturingScreenshot(true);
    try {
      // Hide the feedback button before capturing
      const button = document.getElementById('beta-feedback-button');
      if (button) button.style.display = 'none';
      
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.5,
        logging: false,
      });
      
      // Restore the button
      if (button) button.style.display = '';
      
      const base64 = canvas.toDataURL('image/jpeg', 0.5);
      setScreenshot(base64);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
    } finally {
      setCapturingScreenshot(false);
    }
  }, []);

  const handleOpen = async () => {
    setIsOpen(true);
    setActiveTab('feedback');
    await captureScreenshot();
  };

  const handleViewHistory = async () => {
    setActiveTab('history');
    await fetchMyFeedback();
  };

  const resetForm = () => {
    setMessage(null);
    setScreenshot(null);
    setFeedbackForm({ category: 'bug', title: '', description: '' });
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await betaAPI.submitFeedbackAsUser({
        category: feedbackForm.category,
        title: feedbackForm.title,
        description: feedbackForm.description,
        screenshot: screenshot,
        page_url: window.location.href,
        tester_name: user.name,
        tester_email: user.email,
      });
      
      setMessage({ type: 'success', text: 'Bedankt voor je feedback!' });
      setFeedbackForm({ category: 'bug', title: '', description: '' });
      setScreenshot(null);
      
      // Auto close after success
      setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Er is een fout opgetreden',
      });
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return <Bug className="h-4 w-4" />;
      case 'suggestion': return <Lightbulb className="h-4 w-4" />;
      case 'feature': return <Sparkles className="h-4 w-4" />;
      default: return <MessageSquarePlus className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700',
      wont_fix: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      new: 'Nieuw',
      in_progress: 'In behandeling',
      resolved: 'Opgelost',
      wont_fix: 'Niet oplossen',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${styles[status] || styles.new}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Don't render if beta is disabled
  if (!betaEnabled) {
    return null;
  }
  
  // Only show button to authenticated beta testers
  if (!isAuthenticated || !user?.is_beta_tester) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        id="beta-feedback-button"
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all hover:scale-105"
        data-testid="beta-feedback-button"
      >
        <MessageSquarePlus className="h-5 w-5" />
        <span className="font-medium">Beta Feedback</span>
      </button>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <MessageSquarePlus className="h-5 w-5 text-purple-600" />
              Beta Testing Feedback
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {activeTab === 'history' 
                ? 'Bekijk je ingediende feedback en reacties'
                : 'Help ons de applicatie te verbeteren'}
            </DialogDescription>
          </DialogHeader>

          {/* Message */}
          {message && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
              )}
              {message.text}
            </div>
          )}

          <div className="space-y-4">
            {/* User Info & Tab Toggle */}
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{user?.name}</span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleViewHistory}
                  className={`text-xs px-2 py-1 rounded ${activeTab === 'history' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <History className="h-3 w-3 inline mr-1" />
                  Mijn Feedback
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'history' ? (
              /* Feedback History Tab */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 text-sm">Mijn Ingediende Feedback</h3>
                  <button
                    onClick={() => setActiveTab('feedback')}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    + Nieuwe feedback
                  </button>
                </div>
                
                {loadingFeedback ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : myFeedback.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Je hebt nog geen feedback ingediend.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {myFeedback.map((fb) => (
                      <div key={fb.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(fb.category)}
                            <span className="font-medium text-sm text-gray-900">{fb.title}</span>
                          </div>
                          {getStatusBadge(fb.status)}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{fb.description}</p>
                        {fb.admin_response && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-100">
                            <div className="flex items-center gap-1 text-xs text-purple-700 font-medium">
                              <MessageCircle className="h-3 w-3" />
                              Admin reactie:
                            </div>
                            <p className="text-xs text-purple-600 mt-1">{fb.admin_response}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(fb.created_at).toLocaleDateString('nl-BE')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Feedback Form Tab */
              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categorie</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'bug', icon: Bug, label: 'Bug', color: 'red' },
                      { value: 'suggestion', icon: Lightbulb, label: 'Suggestie', color: 'yellow' },
                      { value: 'feature', icon: Sparkles, label: 'Feature', color: 'purple' },
                    ].map(({ value, icon: Icon, label, color }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFeedbackForm({ ...feedbackForm, category: value })}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          feedbackForm.category === value
                            ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <Icon className="h-5 w-5 mb-1" />
                        <span className="text-xs font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                  <Input
                    type="text"
                    value={feedbackForm.title}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
                    placeholder="Korte beschrijving van je feedback"
                    required
                    data-testid="feedback-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                  <textarea
                    value={feedbackForm.description}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
                    placeholder="Beschrijf het probleem of je suggestie in detail..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    required
                    data-testid="feedback-description-input"
                  />
                </div>

                {/* Screenshot Preview */}
                {screenshot && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Screenshot (automatisch)
                    </label>
                    <div className="relative">
                      <img
                        src={screenshot}
                        alt="Screenshot"
                        className="w-full rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow hover:bg-gray-100"
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Retake Screenshot */}
                {!screenshot && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={captureScreenshot}
                    disabled={capturingScreenshot}
                    className="w-full"
                  >
                    {capturingScreenshot ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Screenshot maken...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4 mr-2" />
                        Screenshot maken
                      </>
                    )}
                  </Button>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="submit-feedback-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Versturen...
                    </>
                  ) : (
                    'Feedback versturen'
                  )}
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BetaFeedbackButton;
